import React, { useEffect, useState } from 'react';
import { apiCall, sendAuthDataViaPostMessage } from '../../utils/api';
import eyeIcon from '../../images/eye-icon.svg';
import eyeIconSlashed from '../../images/eye-slash-icon.svg';
import googleIcon from '../../images/google-logo.svg';
import trackEvent from '../../utils/analytics.js';
import './login.css';

const Login = ({ onClose }) => {
    const [isLogin, setIsLogin] = useState(true);
    const [showPassword, setShowPassword] = useState(false);
    const [isAndroidSource, setIsAndroidSource] = useState(false);
    const [isMahabharatCategory, setIsMahabharatCategory] = useState(false);
    const uuid = localStorage.getItem('user_id');
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        name: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({
        email: '',
        password: '',
        name: '',
        general: ''
    });

    useEffect(() => {
        // Check if UTM source is android
        const urlParams = new URLSearchParams(window.location.search);
        const utmSource = urlParams.get('source');
        const categoryParam = urlParams.get('category');
        setIsAndroidSource(utmSource === 'android');
        setIsMahabharatCategory(
            typeof categoryParam === 'string' && categoryParam.toLowerCase() === 'mahabharat'
        );
        setIsLogin(!utmSource === 'android');
    }, []);

    const setTokenCookies = (data) => {
        const accessTokenExpires = new Date(Date.now() + 1 * 864e5).toUTCString(); // 1 day in ms
        const refreshTokenExpires = new Date(Date.now() + 7 * 864e5).toUTCString(); // 7 days in ms

        document.cookie = `access_token=${data.access_token}; path=/; expires=${accessTokenExpires}`;
        document.cookie = `refresh_token=${data.refresh_token}; path=/; expires=${refreshTokenExpires}`;
        
        try {
            window?.AndroidBridge?.updateFcmToken();
        } catch (error) {
            console.error('Error updating FCM token via Android bridge:', error);
            // Continue execution even if FCM token update fails
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear specific field error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: '',
                general: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {
            email: '',
            password: '',
            name: '',
            general: ''
        };

        // Email validation
        if (!formData.email || formData.email.trim() === '') {
            newErrors.email = 'Email is required';
        } else {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(formData.email)) {
                newErrors.email = 'Please enter a valid email address';
            }
        }

        // Password validation
        if (!formData.password || formData.password.trim() === '') {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters long';
        }

        // Name validation for signup
        if (!isLogin && (!formData.name || formData.name.trim() === '')) {
            newErrors.name = 'Name is required';
        }

        setErrors(newErrors);
        return !Object.values(newErrors).some(error => error !== '');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        // Track login/signup attempt
        trackEvent(isLogin ? 'login_attempted' : 'signup_attempted', {});

        setIsLoading(true);
        setErrors({
            email: '',
            password: '',
            name: '',
            general: ''
        });

        try {
            const endpoint = isLogin ? '/users/login/by/email' : '/users/signup/by/email';
            const payload = isLogin
                ? { email: formData.email, password: formData.password }
                : { 
                    email: formData.email, 
                    password: formData.password, 
                    user_name: formData.name, 
                    uuid: uuid,
                    signupSource: (() => {
                        try {
                            const stored = localStorage.getItem('signupSource');
                            return stored ? JSON.parse(stored) : null;
                        } catch (e) {
                            return null;
                        }
                    })()
                  };

            const response = await apiCall.post(endpoint, JSON.stringify(payload));

            if (response?.status) {
                // Success - call the success callback
                setTokenCookies(response?.data);
                window.location.reload();
            } else {
                setErrors(prev => ({
                    ...prev,
                    general: response.error_message || 'An error occurred'
                }));
            }
        } catch (error) {
            console.error('Auth error:', error);
            setErrors(prev => ({
                ...prev,
                general: 'Network error. Please try again.'
            }));
        } finally {
            setIsLoading(false);
        }
    };

    const toggleMode = () => {
        setIsLogin(!isLogin);
        setErrors({
            email: '',
            password: '',
            name: '',
            general: ''
        });
        setFormData({
            email: '',
            password: '',
            name: ''
        });
    };

    useEffect(() => {
        window.onGoogleSignInResult = async (googleAccessToken, idToken) => {
            if (!googleAccessToken && !idToken) {
                console.error('Google sign-in: no token received from Android');
                return;
            }
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_BASE_URL}/users/generate/tokens`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            google_access_token: googleAccessToken || null,
                            id_token: idToken || null,
                            uuid: localStorage.getItem('user_id'),
                            signupSource: (() => {
                                try {
                                    const stored = localStorage.getItem('signupSource');
                                    return stored ? JSON.parse(stored) : null;
                                } catch (e) { return null; }
                            })()
                        })
                    }
                );
                const data = await response.json();
                if (data.status && data.data) {
                    setTokenCookies(data.data);
                    trackEvent('google_signin_success', {});
                    window.location.reload();
                }
            } catch (err) {
                console.error('Google sign-in token exchange failed:', err);
            }
        };
        return () => { delete window.onGoogleSignInResult; };
    }, []);

    const onGoogleSignIn = async () => {
        console.log("Android sign in triggered");
        if (window?.AndroidBridge?.triggerGoogleLogin) {
            window.AndroidBridge.triggerGoogleLogin();
            return;
        } else {
            throw new Error("AndroidBridge not available");
        }
    };

    const handleGoogleSignIn = async () => {
        trackEvent('google_signin_attempted', {});
        try {
            await onGoogleSignIn();
            return;
        } catch (error) {
            console.log("Android sign-in not available, proceeding with web OAuth");
        }
        const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
        const redirectUri = encodeURIComponent(process.env.REACT_APP_REDIRECT_URI);
        const scope = encodeURIComponent('email profile');
        const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=token&scope=${scope}`;
        window.location.href = googleAuthUrl;
    };

    return (
        <div className="email-auth-container">
            <form onSubmit={handleSubmit} className="email-auth-form">
                {!isLogin && (
                    <div className="form-group">
                        <input
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder="Enter your full name"
                            className={errors.name ? 'error' : ''}
                        />
                        {errors.name && <div className="field-error">{errors.name}</div>}
                    </div>
                )}

                <div className="form-group">
                    <input
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="Enter your email"
                        className={errors.email ? 'error' : ''}
                    />
                    {errors.email && <div className="field-error">{errors.email}</div>}
                </div>

                <div className="form-group">
                    <div
                        className="password-input-container"
                        style={{
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        <input
                            type={showPassword ? "text" : "password"}
                            id="password"
                            name="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            placeholder="Enter your password"
                            className={errors.password ? 'error' : ''}
                            style={{
                                paddingRight: '45px',
                                width: '100%'
                            }}
                        />
                        {formData.password && <img
                            src={showPassword ? eyeIconSlashed : eyeIcon}
                            onClick={() => setShowPassword(!showPassword)}
                            style={{
                                position: 'absolute',
                                right: '12px',
                                width: '20px',
                                height: '20px',
                                cursor: 'pointer',
                                opacity: 0.6,
                                transition: 'opacity 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.target.style.opacity = '1'}
                            onMouseLeave={(e) => e.target.style.opacity = '0.6'}
                            alt={showPassword ? "Hide password" : "Show password"}
                        />}
                    </div>
                    {errors.password && <div className="field-error">{errors.password}</div>}
                </div>

                {errors.general && <div className="general-error">{errors.general}</div>}

                <button
                    type="submit"
                    className="submit-btn"
                    disabled={isLoading}
                >
                    {isLoading ? 'Please wait...' : (isLogin ? 'Log in' : 'Create Account')}
                </button>

                {/* Google Sign-In hidden for now */}
            </form>

            <div className="auth-toggle">
                <p>
                    {isLogin ? "Don't have an account? " : "Already have an account? "}
                    <button
                        type="button"
                        className="toggle-btn"
                        onClick={toggleMode}
                        disabled={isLoading}
                    >
                        {isLogin ? 'Create Account' : 'Log in'}
                    </button>
                </p>
            </div>
        </div>
    );
};

export default Login;